import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SectionTitle from '@/components/feature/SectionTitle';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { comments } from '@/mocks/comments';
import NotFound from '../NotFound';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.slug === id);

  if (!article) {
    return <NotFound />;
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 4);
  const articleComments = comments.filter((c) => c.articleId === article.id);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.imageUrl,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@id': 'http://lalaxjk.cn/#organization',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://lalaxjk.cn/article/${article.slug}`,
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      <Navbar />
      <main className="pt-16">
        {/* Article Header */}
        <section className="bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: category?.name || '文章', path: category ? `/category/${category.slug}` : undefined },
                { label: '文章详情' },
              ]}
            />
          </div>
        </section>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto px-4 md:px-8 pb-12">
          {/* Title & Meta */}
          <header className="mb-8">
            <div className="flex flex-wrap gap-2 mb-4">
              {article.isExclusive && (
                <span className="px-2.5 py-1 bg-accent-500 text-background-50 text-xs font-semibold rounded-md">
                  独家首发
                </span>
              )}
              {article.isHot && (
                <span className="px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-semibold rounded-md">
                  热榜
                </span>
              )}
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 bg-background-100 text-foreground-600 text-xs rounded-md border border-background-200/70"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="font-medium text-foreground-700">{article.author}</span>
              <span>{article.publishDate}</span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" />
                {article.viewCount > 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount} 阅读
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-message-3-line" />
                {article.commentCount} 评论
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="relative aspect-video rounded-2xl overflow-hidden mb-8">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-foreground-600 leading-relaxed mb-6 font-medium">
              {article.summary}
            </p>
            <div className="text-foreground-700 leading-relaxed whitespace-pre-line">
              {article.content.split('\n\n').map((paragraph, index) => (
                <p key={index} className="mb-4 text-base leading-7">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          {/* Share & Tags */}
          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">分享至：</span>
                <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-50 hover:text-primary-600 transition-colors" aria-label="分享到微博">
                  <i className="ri-weibo-line" />
                </button>
                <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-50 hover:text-primary-600 transition-colors" aria-label="分享到微信">
                  <i className="ri-wechat-line" />
                </button>
                <button className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-50 hover:text-primary-600 transition-colors" aria-label="复制链接">
                  <i className="ri-link" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-8">
              <SectionTitle
                title="相关推荐"
                subtitle="RELATED"
                action={{ label: '查看更多', href: `/category/${category?.slug || 'meiri-chigua'}` }}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} variant="compact" />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Comments */}
        {articleComments.length > 0 && (
          <section className="py-10 md:py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-8">
              <SectionTitle title={`网友热评 (${articleComments.length})`} subtitle="COMMENTS" />
              <div className="space-y-4">
                {articleComments.map((comment) => (
                  <div
                    key={comment.id}
                    className="bg-background-50 rounded-xl border border-background-200/70 p-5"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={comment.avatar}
                        alt={comment.username}
                        className="w-10 h-10 rounded-full object-cover bg-background-200"
                        loading="lazy"
                      />
                      <div>
                        <p className="text-sm font-semibold text-foreground-800">{comment.username}</p>
                        <p className="text-xs text-foreground-400">{comment.date}</p>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">{comment.content}</p>
                    <div className="mt-3 flex items-center gap-4 text-xs text-foreground-400">
                      <button className="flex items-center gap-1 hover:text-primary-600 transition-colors">
                        <i className="ri-thumb-up-line" />
                        {comment.likes}
                      </button>
                      <button className="flex items-center gap-1 hover:text-primary-600 transition-colors">
                        <i className="ri-reply-line" />
                        回复
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}